import { NetworkIcon } from "@/components/NetworkIcons";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { CurrencyIcon } from "@/components/ui/CurrencyIcon";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SFIcon } from "@/components/ui/sf-icon";
import { ErrorState } from "@/components/user/foundation/ErrorState";
import { useExternalAccounts } from "@/features/user/wallet/hooks/useWallet";
import { useToast } from "@/hooks/use-toast";
import { useCustodialWallets } from "@/hooks/useCustodialWallets";
import { getAddressFormat, validateAddress } from "@/lib/addr/validate";
import { trackUX, UX_EVENTS } from "@/lib/analytics/ux-events";
import { getPayoutRoutes } from "@/lib/rpc/user/payoutRoutes";
import { sfIcons } from "@/lib/sf-icons";
import { cn } from "@/lib/utils";
import { UX_LEXICON } from "@/lib/ux-lexicon";
import { semanticTokens } from "@packages/config/theme";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

interface CreateAddressFormProps {
  bridgeCustomerId?: string | null;
  customerId?: string | null;
  onCreated?: (data?: unknown) => void;
  className?: string;
  /** 
   * For Auto-Pilot (liquidation) flow: the payout_route UUID that links to the bank account.
   * When provided, creates a liquidation address that auto-sends funds to the linked bank.
   */
  payoutRouteId?: string | null;
  /** @deprecated Use payoutRouteId instead */
  externalAccountId?: string | null;
}

// Supported Assets (8 total) - PRD-02-liquidation-address-supported-assets.md
const SUPPORTED_ASSETS = [
  { value: "USDC", label: "USDC - USD Coin" },
  { value: "USDT", label: "USDT - Tether" },
  { value: "PYUSD", label: "PYUSD - PayPal USD" },
  { value: "USDB", label: "USDB - Bridge's native stablecoin" },
  { value: "EURC", label: "EURC - Euro Coin" },
  { value: "DAI", label: "DAI - Dai stablecoin" },
  { value: "USDP", label: "USDP - USD+ stablecoin" },
  { value: "ETH", label: "ETH - Ethereum" },
] as const;

// Supported Networks (9 total) - PRD-02-liquidation-address-supported-assets.md
const NETWORK_LABELS: Record<string, string> = {
  "ethereum": "Ethereum",
  "solana": "Solana",
  "polygon": "Polygon",
  "arbitrum": "Arbitrum",
  "optimism": "Optimism",
  "base": "Base",
  "avalanche": "Avalanche (C-Chain)",
  "stellar": "Stellar",
  "tron": "Tron",
};

// Network values for Bridge API (lowercase)
const NETWORK_VALUES = Object.keys(NETWORK_LABELS);

// Asset-Network Compatibility Matrix - PRD-02-liquidation-address-supported-assets.md
const ASSET_NETWORK_COMPATIBILITY: Record<string, string[]> = {
  "USDC": ["ethereum", "solana", "polygon", "arbitrum", "optimism", "base", "avalanche", "stellar"],
  "USDT": ["ethereum", "tron"],
  "PYUSD": ["ethereum", "solana"],
  "USDB": ["base", "solana"],
  "EURC": ["base", "solana"],
  "DAI": ["ethereum"],
  "USDP": ["solana"],
  "ETH": ["ethereum"],
};

// Supported Fiat Currencies - PRD-02-liquidation-address-supported-assets.md
// Note: Edge Function currently supports only USD and EUR
const SUPPORTED_CURRENCIES = [
  { value: "USD", label: "USD - US Dollar" },
  { value: "EUR", label: "EUR - Euro" },
  // MXN and BRL are supported by Bridge API but not yet in Edge Function schema
  // { value: "MXN", label: "MXN - Mexican Peso" },
  // { value: "BRL", label: "BRL - Brazilian Real (Beta)" },
] as const;

// Network mapping: UI value → Bridge API chain name
const NETWORK_TO_CHAIN_MAP: Record<string, string> = {
  "ethereum": "ethereum",
  "solana": "solana",
  "polygon": "polygon",
  "arbitrum": "arbitrum",
  "optimism": "optimism",
  "base": "base",
  "avalanche": "avalanche",
  "stellar": "stellar",
  "tron": "tron",
};

// Network to Chain type mapping for validation
const NETWORK_TO_CHAIN_TYPE: Record<string, Chain> = {
  "ethereum": "evm",
  "polygon": "evm",
  "arbitrum": "evm",
  "optimism": "evm",
  "base": "evm",
  "avalanche": "evm",
  "solana": "sol",
  "tron": "tron",
  "stellar": "stellar",
};

export default function CreateAddressForm({
  bridgeCustomerId,
  onCreated,
  className,
  payoutRouteId,
  externalAccountId // deprecated, kept for backward compat
}: CreateAddressFormProps) {
  const { toast } = useToast();
  const [asset, setAsset] = useState<string>("USDC"); // Default to USDC for better UX
  const [network, setNetwork] = useState<string>("ethereum"); // Default to Ethereum
  const [currency, setCurrency] = useState<string>("USD"); // P2: Fiat Settlement Currency
  const [label, setLabel] = useState<string>(""); // P2: Optional label
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // NEW: Destination type selection
  const [destinationType, setDestinationType] = useState<'fiat' | 'crypto' | 'wallet'>('fiat');

  // NEW: Fiat destination fields
  const [selectedPayoutRouteId, setSelectedPayoutRouteId] = useState<string>(payoutRouteId || "");
  const [selectedExternalAccountId, setSelectedExternalAccountId] = useState<string>("");

  // NEW: Crypto destination fields
  const [destinationAddress, setDestinationAddress] = useState<string>("");
  const [destinationChain, setDestinationChain] = useState<string>("ethereum");
  const [destinationCurrency, setDestinationCurrency] = useState<string>("USD");
  const [destinationAddressError, setDestinationAddressError] = useState<string | null>(null);

  // NEW: Wallet destination fields
  const [selectedWalletId, setSelectedWalletId] = useState<string>("");

  // Determine if this is a liquidation (auto-payout) flow
  const isLiquidationFlow = !!payoutRouteId || !!externalAccountId || destinationType === 'fiat';

  // P1: Check for payout routes (required for fiat destinations)
  const { data: payoutRoutesData, isLoading: payoutRoutesLoading } = useQuery({
    queryKey: ['payout-routes-for-address'],
    queryFn: () => getPayoutRoutes({ limit: 10 }),
    enabled: destinationType === 'fiat' && !payoutRouteId, // Load when fiat destination selected
    retry: 1,
  });

  // Also check for external accounts (user might have accounts but no payout routes yet)
  // Only load when fiat destination is selected to avoid unnecessary API calls
  const { data: externalAccountsData, isLoading: externalAccountsLoading, error: externalAccountsError } = useExternalAccounts({
    enabled: destinationType === 'fiat', // Only load when fiat destination selected
    retry: 1,
    onError: (error) => {
      // Silently handle errors - external accounts are optional
      console.warn('[CreateAddressForm] Failed to load external accounts:', error);
    }
  });

  const payoutRoutes = useMemo(() => {
    if (!payoutRoutesData?.data) return [];
    return Array.isArray(payoutRoutesData.data) ? payoutRoutesData.data : [];
  }, [payoutRoutesData]);

  const externalAccounts = useMemo(() => {
    if (!externalAccountsData) return [];
    return Array.isArray(externalAccountsData) ? externalAccountsData : [];
  }, [externalAccountsData]);

  const hasPayoutRoute = useMemo(() => {
    if (payoutRouteId) return true; // Explicitly provided
    if (selectedPayoutRouteId) return true; // User selected
    return payoutRoutes.length > 0;
  }, [payoutRouteId, selectedPayoutRouteId, payoutRoutes]);

  // Check if user has any external accounts (even if no payout routes)
  const hasExternalAccounts = useMemo(() => {
    return externalAccounts.length > 0;
  }, [externalAccounts]);

  // Check if we have either payout route OR external account selected
  const hasFiatDestination = useMemo(() => {
    return hasPayoutRoute || !!selectedExternalAccountId;
  }, [hasPayoutRoute, selectedExternalAccountId]);

  // Check if we're still loading both
  const isLoadingAccounts = payoutRoutesLoading || externalAccountsLoading;

  // NEW: Load custodial wallets for wallet destination
  const { wallets, loading: walletsLoading, error: walletsError } = useCustodialWallets();

  const hasWallets = useMemo(() => {
    return wallets.length > 0;
  }, [wallets]);

  // P1: Filter networks based on selected asset (compatibility matrix)
  const compatibleNetworks = useMemo(() => {
    if (!asset) return [];
    return ASSET_NETWORK_COMPATIBILITY[asset] || [];
  }, [asset]);

  // Reset network if current selection is incompatible with new asset
  useEffect(() => {
    if (asset && compatibleNetworks.length > 0 && !compatibleNetworks.includes(network)) {
      setNetwork(compatibleNetworks[0]); // Set to first compatible network
    }
  }, [asset, compatibleNetworks, network]);

  // Map UI network to Bridge API chain name
  const bridgeChain = useMemo(() => {
    return NETWORK_TO_CHAIN_MAP[network] || network.toLowerCase();
  }, [network]);

  // Form validation - must be before any early returns (rules of hooks)
  const canSubmit = useMemo(() => {
    if (submitting) return false;
    if (!asset || !network) return false;

    if (destinationType === 'fiat') {
      // Either payout route OR external account must be selected
      if (!hasFiatDestination) return false;
    }
    if (destinationType === 'crypto' && (!destinationAddress.trim() || !destinationChain || !destinationCurrency)) return false;
    if (destinationType === 'wallet' && !selectedWalletId) return false;

    if (destinationAddressError) return false;

    return true;
  }, [submitting, asset, network, destinationType, hasFiatDestination, destinationAddress, destinationChain, destinationCurrency, destinationAddressError, selectedWalletId]);

  // NEW: Validate destination address (for crypto)
  const validateDestinationAddress = (address: string): boolean => {
    if (!address.trim()) {
      setDestinationAddressError("Destination address is required");
      return false;
    }

    const chainType = NETWORK_TO_CHAIN_TYPE[destinationChain] || 'evm';
    const isValid = validateAddress(chainType, address.trim());

    if (!isValid) {
      setDestinationAddressError(`Invalid address format for ${NETWORK_LABELS[destinationChain] || destinationChain}. ${getAddressFormat(chainType)}`);
      return false;
    }

    setDestinationAddressError(null);
    return true;
  };

  // NEW: Validate destination address when destination chain changes
  useEffect(() => {
    if (destinationAddress) {
      validateDestinationAddress(destinationAddress);
    }
  }, [destinationChain, destinationAddress]);

  const createAddress = async () => {
    setSubmitting(true);
    setError(null);

    // NEW: Validate destination-specific fields
    if (destinationType === 'fiat' && !hasPayoutRoute) {
      setError(new Error("Please select a bank account for fiat destination."));
      setSubmitting(false);
      return false;
    }

    if (destinationType === 'crypto') {
      if (!validateDestinationAddress(destinationAddress)) {
        setSubmitting(false);
        return false;
      }
      if (!destinationChain || !destinationCurrency) {
        setError(new Error("Please select destination chain and currency for crypto destination."));
        setSubmitting(false);
        return false;
      }
    }

    if (destinationType === 'wallet' && !selectedWalletId) {
      setError(new Error("Please select a wallet for wallet destination."));
      setSubmitting(false);
      return false;
    }

    // Validation: Check asset-network compatibility
    if (asset && !compatibleNetworks.includes(network)) {
      setError(new Error(`Network "${NETWORK_LABELS[network] || network}" is not compatible with asset "${asset}". Please select a compatible network.`));
      setSubmitting(false);
      return false;
    }

    try {
      // NEW: Use liquidation-address-create Edge Function for all destination types
      const { supabase } = await import('@/integrations/supabase/client');

      // Build request body based on destination type
      // Note: return_address will be generated automatically by Edge Function
      let requestBody: Record<string, unknown> = {
        destination_type: destinationType,
        chain: bridgeChain, // Required for return_address generation
        asset: asset.toLowerCase(), // Required for return_address generation
        ...(label && { label }),
      };

      if (destinationType === 'fiat') {
        let externalAccountId: string | undefined;
        let paymentRail: string = 'ach';
        let destinationCurrency: string = currency.toLowerCase();

        // Check if user selected external account directly (when no payout routes available)
        if (selectedExternalAccountId) {
          const selectedAccount = externalAccounts.find(acc => acc.id === selectedExternalAccountId);
          if (!selectedAccount) {
            throw new Error("Selected external account not found");
          }
          externalAccountId = selectedAccount.external_account_id;
          // Determine payment rail from account type
          if (selectedAccount.account_type === 'iban') {
            paymentRail = 'sepa';
          } else if (selectedAccount.account_type === 'spei') {
            paymentRail = 'spei';
          } else {
            paymentRail = 'ach'; // Default for US accounts
          }
          destinationCurrency = selectedAccount.currency?.toLowerCase() || currency.toLowerCase();
        } else {
          // Use payout route
          const selectedRoute = payoutRoutes.find(r => r.id === (payoutRouteId || selectedPayoutRouteId));
          if (!selectedRoute) {
            throw new Error("Selected payout route not found");
          }
          // Extract external_account_id from payout route metadata
          externalAccountId = selectedRoute.metadata?.external_account_id || selectedRoute.metadata?.bridge_external_account_id;
          if (!externalAccountId) {
            throw new Error("Payout route does not have an associated external account. Please re-link your bank account.");
          }
          paymentRail = selectedRoute.metadata?.rail || selectedRoute.metadata?.payment_rail || 'ach';
          destinationCurrency = (selectedRoute.metadata?.currency || currency).toLowerCase();
        }

        if (!externalAccountId) {
          throw new Error("External account ID is required for fiat destination");
        }

        requestBody = {
          ...requestBody,
          external_account_id: externalAccountId,
          destination_payment_rail: paymentRail,
          destination_currency: destinationCurrency,
        };
      } else if (destinationType === 'crypto') {
        requestBody = {
          ...requestBody,
          destination_address: destinationAddress.trim(),
          destination_chain: NETWORK_TO_CHAIN_MAP[destinationChain] || destinationChain.toLowerCase(),
          destination_currency: destinationCurrency.toLowerCase(),
        };
      } else if (destinationType === 'wallet') {
        const selectedWallet = wallets.find(w => w.id === selectedWalletId);
        if (!selectedWallet) {
          throw new Error("Selected wallet not found");
        }
        if (!selectedWallet.bridge_wallet_id) {
          throw new Error("Selected wallet does not have a Bridge wallet ID. Please try selecting another wallet.");
        }
        requestBody = {
          ...requestBody,
          wallet_id: selectedWallet.bridge_wallet_id,
          chain: bridgeChain, // Chain for the liquidation address (where funds will be received)
          currency: asset.toLowerCase(), // Currency for the liquidation address (e.g., 'usdc')
        };
      }

      // Call liquidation-address-create Edge Function
      const { data, error: edgeError } = await supabase.functions.invoke('liquidation-address-create', {
        body: requestBody
      });

      if (edgeError) {
        throw new Error(edgeError.message || 'Failed to create liquidation address');
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      // TASK-009D: Track address_created event
      trackUX(UX_EVENTS.ADDRESS_CREATED, {
        asset,
        network: bridgeChain,
        source: 'dashboard',
        type: 'liquidation',
        destination_type: destinationType,
        currency: currency || undefined,
        has_label: !!label,
      });

      toast({
        title: "Liquidation address created",
        description: destinationType === 'fiat'
          ? "Your receiving address is ready. Funds will auto-convert to fiat and send to your bank."
          : destinationType === 'crypto'
            ? "Your receiving address is ready. Funds will auto-convert and send to the crypto address."
            : "Your receiving address is ready. Funds will auto-convert and send to your wallet."
      });

      onCreated?.(data);
      return true;
    } catch (error: unknown) {
      const errorObj = error instanceof Error ? error : new Error("Failed to create address. Please try again.");
      setError(errorObj);
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await createAddress();
  };

  const handleRetry = async () => {
    setError(null);
    await createAddress();
  };

  // Show error state if error occurred
  if (error && error.message !== "Please add a bank account before creating a liquidation address.") {
    return (
      <ErrorState
        error={error}
        errorType="address_creation_failed"
        surface="create_address_form"
        onRetry={handleRetry}
        retryLabel="Try Again"
        title={UX_LEXICON.address.error.title}
        message={UX_LEXICON.address.error.message}
        secondaryAction={{
          label: "Cancel",
          onClick: () => setError(null),
          variant: "outline"
        }}
        className={className}
      />
    );
  }

  return (
    <form onSubmit={handleSubmit} className={className} aria-label="Create liquidation address form">
      {/* P0: Warning о неправильном адресе/chain - КРИТИЧЕСКОЕ ТРЕБОВАНИЕ из acceptance-criteria.md */}
      <Alert variant="warning" className="mb-4">
        <SFIcon icon={sfIcons.alertTriangle} size="md" />
        <AlertTitle className={cn(semanticTokens.typography.label, "font-semibold")}>
          Irreversible Loss Risk
        </AlertTitle>
        <AlertDescription className={semanticTokens.typography.body}>
          Sending funds to the wrong address or network can result in permanent loss of funds.
          Double-check the address and network before sending.
        </AlertDescription>
      </Alert>

      {/* P0.5: Second Warning - Always show verification reminder */}
      <Alert variant="warning" className="mb-4">
        <SFIcon icon={sfIcons.alertTriangle} size="md" />
        <AlertTitle className={cn(semanticTokens.typography.label, "font-semibold")}>
          Verify Before Sending
        </AlertTitle>
        <AlertDescription className={semanticTokens.typography.body}>
          Once you generate an address, verify that you're sending the correct asset ({asset || 'selected asset'})
          to the correct network ({network ? NETWORK_LABELS[network] : 'selected network'}).
          Transactions cannot be reversed.
        </AlertDescription>
      </Alert>

      {/* NEW: Destination Type Selection */}
      <div className="space-y-3 mb-6">
        <Label className="text-base font-semibold">
          Destination Type <span className="text-destructive">*</span>
        </Label>
        <RadioGroup
          value={destinationType}
          onValueChange={(value) => {
            setDestinationType(value as 'fiat' | 'crypto' | 'wallet');
            // Reset dependent fields when type changes
            setSelectedPayoutRouteId("");
            setDestinationAddress("");
            setSelectedWalletId("");
          }}
          className={cn("space-y-3", semanticTokens.spacing.md.replace('p-', 'space-y-'))}
        >
          <div className={cn(
            "flex items-start space-x-3 p-4 border rounded-lg cursor-pointer",
            "hover:bg-muted/50 transition-colors",
            destinationType === 'fiat' && "border-primary bg-primary/5",
            semanticTokens.border.default,
            semanticTokens.radius.lg
          )}>
            <RadioGroupItem value="fiat" id="dest-fiat" className="mt-1" />
            <Label htmlFor="dest-fiat" className="flex-1 cursor-pointer">
              <div className="flex items-center gap-2 mb-1">
                <SFIcon icon={sfIcons.building2} size="md" aria-hidden="true" />
                <span className="font-medium">Fiat (Bank Account)</span>
              </div>
              <p className={cn("text-sm", semanticTokens.fg.muted)}>
                Convert to fiat and send to your bank account
              </p>
            </Label>
          </div>

          <div className={cn(
            "flex items-start space-x-3 p-4 border rounded-lg cursor-pointer",
            "hover:bg-muted/50 transition-colors",
            destinationType === 'crypto' && "border-primary bg-primary/5",
            semanticTokens.border.default,
            semanticTokens.radius.lg
          )}>
            <RadioGroupItem value="crypto" id="dest-crypto" className="mt-1" />
            <Label htmlFor="dest-crypto" className="flex-1 cursor-pointer">
              <div className="flex items-center gap-2 mb-1">
                <SFIcon icon={sfIcons.mapPin} size="md" aria-hidden="true" />
                <span className="font-medium">Crypto Address</span>
              </div>
              <p className={cn("text-sm", semanticTokens.fg.muted)}>
                Convert and send to a crypto address
              </p>
            </Label>
          </div>

          <div className={cn(
            "flex items-start space-x-3 p-4 border rounded-lg cursor-pointer",
            "hover:bg-muted/50 transition-colors",
            destinationType === 'wallet' && "border-primary bg-primary/5",
            semanticTokens.border.default,
            semanticTokens.radius.lg
          )}>
            <RadioGroupItem value="wallet" id="dest-wallet" className="mt-1" />
            <Label htmlFor="dest-wallet" className="flex-1 cursor-pointer">
              <div className="flex items-center gap-2 mb-1">
                <SFIcon icon={sfIcons.wallet} size="md" aria-hidden="true" />
                <span className="font-medium">My Wallet</span>
              </div>
              <p className={cn("text-sm", semanticTokens.fg.muted)}>
                Convert and send to your custodial wallet
              </p>
            </Label>
          </div>
        </RadioGroup>
      </div>

      {/* NEW: Fiat Destination - Payout Route Selection */}
      {destinationType === 'fiat' && (
        <div className="space-y-2 mb-4">
          <Label htmlFor="payout-route">
            Bank Account <span className="text-destructive">*</span>
          </Label>
          {isLoadingAccounts ? (
            <div className={cn("p-4 border rounded-lg", semanticTokens.border.default, semanticTokens.radius.lg)}>
              <p className="text-sm text-muted-foreground">Loading bank accounts...</p>
            </div>
          ) : payoutRoutes.length === 0 ? (
            // Check if user has external accounts but no payout routes
            hasExternalAccounts ? (
              <div className="space-y-3">
                <Alert variant="info">
                  <SFIcon icon={sfIcons.info} size="md" />
                  <AlertTitle className="text-base font-semibold">
                    Bank Accounts Found
                  </AlertTitle>
                  <AlertDescription>
                    You have {externalAccounts.length} bank account{externalAccounts.length !== 1 ? 's' : ''} connected. Select one to create a liquidation address.
                  </AlertDescription>
                </Alert>
                <div className="space-y-2">
                  <Label htmlFor="external-account">
                    Select Bank Account <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={selectedExternalAccountId}
                    onValueChange={(value) => {
                      setSelectedExternalAccountId(value);
                      setSelectedPayoutRouteId(""); // Clear payout route selection
                    }}
                  >
                    <SelectTrigger id="external-account" aria-label="Select bank account">
                      <SelectValue placeholder="Select bank account" />
                    </SelectTrigger>
                    <SelectContent>
                      {externalAccounts.map((account) => (
                        <SelectItem key={account.id} value={account.id}>
                          <div>
                            <div className="font-medium">
                              {account.bank_name || 'Bank Account'}
                              {account.account_last4 && ` • ****${account.account_last4}`}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {account.account_owner_name && `${account.account_owner_name} • `}
                              {account.currency || 'USD'} • {account.account_type?.toUpperCase() || 'ACH'}
                              {account.country && ` • ${account.country}`}
                            </div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : (
              <Alert variant="warning">
                <Info className="h-5 w-5" />
                <AlertTitle className="text-base font-semibold">
                  No Bank Accounts
                </AlertTitle>
                <AlertDescription>
                  You need to add a bank account before creating a liquidation address.{' '}
                  <Link
                    to="/dashboard/send"
                    className="underline hover:no-underline font-medium"
                  >
                    Add bank account
                  </Link>
                </AlertDescription>
              </Alert>
            )
          ) : (
            <Select
              value={payoutRouteId || selectedPayoutRouteId}
              onValueChange={(value) => {
                setSelectedPayoutRouteId(value);
                setSelectedExternalAccountId(""); // Clear external account selection
              }}
            >
              <SelectTrigger id="payout-route" aria-label="Select bank account">
                <SelectValue placeholder="Select bank account" />
              </SelectTrigger>
              <SelectContent>
                {payoutRoutes.map((route) => (
                  <SelectItem key={route.id} value={route.id}>
                    <div>
                      <div className="font-medium">{route.metadata?.bank_name || route.label || 'Bank Account'}</div>
                      {route.metadata?.account_last4 && (
                        <div className="text-xs text-muted-foreground">
                          ****{route.metadata.account_last4} • {route.metadata?.currency || 'USD'} • {route.metadata?.rail || 'ACH'}
                        </div>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      {/* NEW: Crypto Destination Fields */}
      {destinationType === 'crypto' && (
        <>
          <div className="space-y-2 mb-4">
            <Label htmlFor="destination-address">
              Destination Address <span className="text-destructive">*</span>
            </Label>
            <Input
              id="destination-address"
              value={destinationAddress}
              onChange={(e) => {
                setDestinationAddress(e.target.value);
                if (destinationAddressError) {
                  validateDestinationAddress(e.target.value);
                }
              }}
              onBlur={() => validateDestinationAddress(destinationAddress)}
              placeholder={destinationChain ? getAddressFormat(NETWORK_TO_CHAIN_TYPE[destinationChain] || 'evm') : "0x..."}
              required
              className={cn(destinationAddressError && "border-destructive")}
              aria-invalid={!!destinationAddressError}
              aria-describedby={destinationAddressError ? "destination-address-error" : undefined}
            />
            {destinationAddressError && (
              <p id="destination-address-error" className="text-sm text-destructive flex items-center gap-1">
                <SFIcon icon={sfIcons.alertTriangle} size="sm" />
                {destinationAddressError}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Crypto address where converted funds will be sent
            </p>
          </div>

          <div className="space-y-2 mb-4">
            <Label htmlFor="destination-chain">
              Destination Chain <span className="text-destructive">*</span>
            </Label>
            <Select value={destinationChain} onValueChange={setDestinationChain}>
              <SelectTrigger id="destination-chain" aria-label="Select destination chain" className="flex items-center gap-2">
                {destinationChain && (
                  <NetworkIcon network={destinationChain} className="w-4 h-4 shrink-0" />
                )}
                <SelectValue placeholder="Select chain" />
              </SelectTrigger>
              <SelectContent>
                {NETWORK_VALUES.map((chain) => (
                  <SelectItem key={chain} value={chain}>
                    <div className="flex items-center gap-2">
                      <NetworkIcon network={chain} className="w-4 h-4" />
                      <span>{NETWORK_LABELS[chain] || chain}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2 mb-4">
            <Label htmlFor="destination-currency-crypto">
              Destination Currency <span className="text-destructive">*</span>
            </Label>
            <Select value={destinationCurrency} onValueChange={setDestinationCurrency}>
              <SelectTrigger id="destination-currency-crypto" aria-label="Select destination currency">
                <SelectValue placeholder="Select currency" />
              </SelectTrigger>
              <SelectContent>
                {SUPPORTED_CURRENCIES.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    <div className="flex items-center gap-2">
                      <CurrencyIcon symbol={item.value} size="sm" />
                      <span>{item.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Currency in which funds will be converted
            </p>
          </div>
        </>
      )}

      {/* NEW: Wallet Destination - Wallet Selection */}
      {destinationType === 'wallet' && (
        <div className="space-y-2 mb-4">
          <Label htmlFor="wallet-id">
            Destination Wallet <span className="text-destructive">*</span>
          </Label>
          {walletsLoading ? (
            <div className={cn("p-4 border rounded-lg", semanticTokens.border.default, semanticTokens.radius.lg)}>
              <p className="text-sm text-muted-foreground">Loading wallets...</p>
            </div>
          ) : walletsError ? (
            <Alert variant="destructive">
              <AlertTitle>Failed to load wallets</AlertTitle>
              <AlertDescription>{walletsError.message}</AlertDescription>
            </Alert>
          ) : wallets.length === 0 ? (
            <Alert variant="warning">
              <Info className="h-5 w-5" />
              <AlertTitle className="text-base font-semibold">
                No Wallets Found
              </AlertTitle>
              <AlertDescription>
                You need to create a custodial wallet first.{' '}
                <Link
                  to="/dashboard/wallet"
                  className="underline hover:no-underline font-medium"
                >
                  Create wallet
                </Link>
              </AlertDescription>
            </Alert>
          ) : (
            <Select value={selectedWalletId} onValueChange={setSelectedWalletId}>
              <SelectTrigger id="wallet-id" aria-label="Select wallet">
                <SelectValue placeholder="Select wallet" />
              </SelectTrigger>
              <SelectContent>
                {wallets.map((wallet) => (
                  <SelectItem key={wallet.id} value={wallet.id}>
                    <div>
                      <div className="font-medium">{NETWORK_LABELS[wallet.chain] || wallet.chain} Wallet</div>
                      <div className="text-xs text-muted-foreground font-mono">
                        {wallet.address.slice(0, 8)}...{wallet.address.slice(-8)}
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      {/* Asset Selection */}
      <div className="space-y-2 mb-4">
        <Label htmlFor="create-address-asset" className="mb-2 block">
          Asset <span className="text-destructive">*</span>
        </Label>
        <Select value={asset} onValueChange={setAsset}>
          <SelectTrigger id="create-address-asset" aria-label="Select cryptocurrency asset">
            <SelectValue placeholder="Select asset" />
          </SelectTrigger>
          <SelectContent>
            {SUPPORTED_ASSETS.map((item) => (
              <SelectItem key={item.value} value={item.value}>
                <div className="flex items-center gap-2">
                  <CurrencyIcon symbol={item.value} size="sm" />
                  <span>{item.label}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Network Selection (filtered by asset compatibility) */}
      <div className="space-y-2 mb-4">
        <Label htmlFor="create-address-network" className="mb-2 block">
          Network <span className="text-destructive">*</span>
        </Label>
        <Select
          value={network}
          onValueChange={setNetwork}
          disabled={!asset || compatibleNetworks.length === 0}
        >
          <SelectTrigger
            id="create-address-network"
            aria-label="Select blockchain network"
            aria-disabled={!asset || compatibleNetworks.length === 0}
            className="flex items-center gap-2"
          >
            {network && (
              <NetworkIcon network={network} className="w-4 h-4 shrink-0" />
            )}
            <SelectValue
              placeholder={
                !asset
                  ? "Select asset first"
                  : compatibleNetworks.length === 0
                    ? "No compatible networks"
                    : "Select network"
              }
            />
          </SelectTrigger>
          <SelectContent>
            {compatibleNetworks.map((net) => (
              <SelectItem key={net} value={net}>
                <div className="flex items-center gap-2">
                  <NetworkIcon network={net} className="w-4 h-4" />
                  <span>{NETWORK_LABELS[net] || net}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {asset && compatibleNetworks.length === 0 && (
          <p className="text-sm text-destructive flex items-center gap-1 mt-1">
            <AlertTriangle className="h-4 w-4" />
            No compatible networks for {asset}. Please select a different asset.
          </p>
        )}
        {asset && compatibleNetworks.length > 0 && compatibleNetworks.length < NETWORK_VALUES.length && (
          <p className="text-xs text-muted-foreground mt-1">
            Showing {compatibleNetworks.length} compatible network{compatibleNetworks.length !== 1 ? 's' : ''} for {asset}
          </p>
        )}
      </div>

      {/* P2: Fiat Settlement Currency (optional) */}
      <div className="space-y-2 mb-4">
        <Label htmlFor="create-address-currency" className="mb-2 block">
          Settlement Currency <span className="text-xs text-muted-foreground">(optional)</span>
        </Label>
        <Select value={currency} onValueChange={setCurrency}>
          <SelectTrigger id="create-address-currency" aria-label="Select fiat settlement currency">
            <SelectValue placeholder="USD (default)" />
          </SelectTrigger>
          <SelectContent>
            {SUPPORTED_CURRENCIES.map((item) => (
              <SelectItem key={item.value} value={item.value}>
                <div className="flex items-center gap-2">
                  <CurrencyIcon symbol={item.value} size="sm" />
                  <span>{item.label}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          Currency in which funds will be converted and sent to your bank account
        </p>
      </div>

      {/* P2: Label (optional) */}
      <div className="space-y-2 mb-4">
        <Label htmlFor="create-address-label" className="mb-2 block">
          Label <span className="text-xs text-muted-foreground">(optional)</span>
        </Label>
        <Input
          id="create-address-label"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g., Main Business Address"
          maxLength={64}
          aria-label="Address label"
        />
        <p className="text-xs text-muted-foreground">
          {label.length}/64 characters {label.length === 64 && "(maximum reached)"}
        </p>
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        className="w-full min-h-[44px] focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
        disabled={!canSubmit}
        aria-label={submitting ? "Creating address, please wait" : "Create liquidation address"}
        aria-busy={submitting}
      >
        {submitting ? "Creating..." : "Create Address"}
      </Button>

      {/* Validation feedback */}
      {!canSubmit && !submitting && (
        <p className="text-sm text-muted-foreground mt-2 text-center">
          {!asset && !network && "Please select an asset and network"}
          {asset && !network && "Please select a network"}
          {asset && network && destinationType === 'fiat' && !hasPayoutRoute && !selectedExternalAccountId && "Please select a bank account"}
          {asset && network && destinationType === 'crypto' && (!destinationAddress.trim() || !destinationChain || !destinationCurrency) && "Please fill in all crypto destination fields"}
          {asset && network && destinationType === 'crypto' && destinationAddress.trim() && destinationAddressError && "Please fix destination address errors"}
          {asset && network && destinationType === 'wallet' && !selectedWalletId && "Please select a wallet"}
        </p>
      )}
    </form>
  );
}
